
import React, { useState } from 'react';
import { Send, Sparkles, Phone, User, CheckCircle2, AlertTriangle } from 'lucide-react';
import { sendLeadToN8N } from '../services/n8nService';
import { sendCriticalAlert } from '../services/notificationService';

const SingleEntry: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(false);
    
    try {
      // Uses the default N8N_WEBHOOKS.RETELL_QUEUE URL from n8nService
      await sendLeadToN8N(formData);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
      setFormData({ firstName: '', lastName: '', phone: '' });
    } catch (err) {
      setError(true);
      const settingsStr = localStorage.getItem('reachai_notification_settings');
      if (settingsStr) {
        const settings = JSON.parse(settingsStr);
        if (settings.alertOnFailure) {
          await sendCriticalAlert(
            'Critical Failure: Single Lead Dispatch',
            `Lead: ${formData.firstName} ${formData.lastName} (${formData.phone})\nError: ${err instanceof Error ? err.message : 'Unknown Network Error'}`
          );
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto transition-colors duration-300">
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl shadow-slate-200 dark:shadow-none border border-slate-100 dark:border-slate-800 overflow-hidden">
        <div className="bg-indigo-600 p-8 text-white relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="text-2xl font-bold">New Caller Assignment</h2>
            <p className="text-indigo-100 mt-2 opacity-80">Manually queue a single high-priority lead for immediate AI processing.</p>
          </div>
          <Sparkles className="absolute top-4 right-8 w-24 h-24 text-indigo-500 opacity-30 -rotate-12" />
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center">
                <User className="w-4 h-4 mr-2 text-slate-400 dark:text-slate-500" /> First Name
              </label>
              <input
                required
                type="text"
                value={formData.firstName}
                onChange={e => setFormData({...formData, firstName: e.target.value})}
                placeholder="John"
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all dark:text-slate-100"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center">
                <User className="w-4 h-4 mr-2 text-slate-400 dark:text-slate-500" /> Last Name
              </label>
              <input
                required
                type="text"
                value={formData.lastName}
                onChange={e => setFormData({...formData, lastName: e.target.value})}
                placeholder="Doe"
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all dark:text-slate-100"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center">
              <Phone className="w-4 h-4 mr-2 text-slate-400 dark:text-slate-500" /> Phone Number
            </label>
            <input
              required
              type="tel"
              value={formData.phone}
              onChange={e => setFormData({...formData, phone: e.target.value})}
              placeholder="+1 555 0123 4567"
              className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all dark:text-slate-100"
            />
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
            <div className="flex flex-col">
              {success && (
                <div className="flex items-center text-emerald-600 dark:text-emerald-400 font-bold animate-in fade-in slide-in-from-left-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Lead Dispatched Successfully
                </div>
              )}
              {error && (
                <div className="flex items-center text-rose-600 dark:text-rose-400 font-bold animate-in fade-in slide-in-from-left-2 text-sm">
                  <AlertTriangle className="w-4 h-4 mr-2" /> Dispatch Failed. Admin Notified.
                </div>
              )}
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-3 bg-indigo-600 text-white font-bold rounded-xl flex items-center space-x-2 shadow-lg shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {isSubmitting ? 'Processing...' : <><Send className="w-4 h-4 mr-2" /> Dispatch Lead</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SingleEntry;
